import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { HeadingTitle, SearchInput } from '../../../components/modules/ui'
import FilterControl from '../../../components/modules/table/FilterControl'
import {
  currentMonth,
  currentYear,
  monthsOptions as months,
  yearsOptions
} from '../../../components/utils/constant'
import GlobalContext from '../../../context/global-context'
import { BankAPI } from '../../../api/bancos.api'
import { BusinessAPI } from '../../../api/business.api'
import { useInfiniteBankTransactions } from '../../../hooks/useInfiniteBankTransactions'
import TableBancoConciliaciones from './TableBancoConciliaciones'
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll'
import ExportToXlsxModal from '../../../components/modules/modals/ExportToXlsxModal'

const ListLibroBancoConciliaciones = () => {
  const { currentBusiness } = useContext(GlobalContext)
  const bankAPI = useMemo(() => new BankAPI(), [])
  const businessAPI = useMemo(() => new BusinessAPI(), [])

  const [monthFilter, setMonthFilter] = useState(currentMonth)
  const [yearFilter, setYearFilter] = useState(currentYear)
  const [accountFilter, setAccountFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [accountOptions, setAccountOptions] = useState([{ value: '', label: 'Todas las cuentas' }])
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [expandedRows, setExpandedRows] = useState(new Set())
  const [linkageByRow, setLinkageByRow] = useState({})
  const [loadingDetails, setLoadingDetails] = useState({})
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  useEffect(() => {
    setExpandedRows(new Set())
    setLinkageByRow({})
  }, [currentBusiness?.id])

  const monthsOptionsWithAll = useMemo(
    () => [
      { value: 0, label: 'Todos los meses' },
      ...months.map(opt => ({
        ...opt,
        disabled: yearFilter === currentYear && opt.value > currentMonth
      }))
    ],
    [yearFilter]
  )

  const fetchAccounts = useCallback(async () => {
    if (!currentBusiness?.id) return
    try {
      const response = await bankAPI.getListBank(currentBusiness.id)
      const list = Array.isArray(response?.data) ? response.data : response?.data?.data || []
      const mapped = list.map(acc => {
        const nombre = acc.bank?.name || acc.nombreCuentaBanco || acc.nombreBanco || 'Banco'
        const numero = acc.numeroCuenta ?? acc.numeroCuentaBanco ?? acc.number ?? acc.numero ?? ''
        const value = acc.id ?? acc.accountIdSql ?? acc.productId ?? (numero || nombre)
        return {
          value: String(value),
          label: numero ? `${nombre} - ${numero}` : nombre
        }
      })

      const unique = []
      const seen = new Set()
      mapped.forEach(opt => {
        if (!seen.has(opt.value)) {
          seen.add(opt.value)
          unique.push(opt)
        }
      })

      setAccountOptions([{ value: '', label: 'Todas las cuentas' }, ...unique])
    } catch (err) {
      console.error('Error al cargar cuentas bancarias', err)
      setAccountOptions([{ value: '', label: 'Todas las cuentas' }])
    }
  }, [bankAPI, currentBusiness?.id])

  useEffect(() => {
    fetchAccounts()
  }, [fetchAccounts])

  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading
  } = useInfiniteBankTransactions({
    businessId: currentBusiness?.id || 0,
    productId: Number(accountFilter) || 0,
    year: yearFilter,
    month: monthFilter,
    limit: 30,
    filters: {
      searchQuery: debouncedSearch,
      account: accountFilter
    }
  })

  const { lastElementRef } = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    threshold: 300
  })

  const rows = useMemo(() => {
    const loadedRows = infiniteData?.pages?.flatMap(page => page.data) || []
    const movementIds = new Set()

    return loadedRows.filter(row => {
      if (row?.id === undefined || row?.id === null) return true

      const id = String(row.id)
      if (movementIds.has(id)) return false

      movementIds.add(id)
      return true
    })
  }, [infiniteData])

  const statusOptions = useMemo(() => {
    const unique = new Set(
      rows
        .map(r => (r?.status ?? '').toString().trim())
        .filter(s => s !== '')
    )

    if (statusFilter) unique.add(statusFilter)

    const sorted = Array.from(unique).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    )

    return [{ value: '', label: 'Todos los estados' }, ...sorted.map(s => ({ value: s, label: s }))]
  }, [rows, statusFilter])

  const filteredRows = useMemo(() => {
    if (!statusFilter) return rows
    return rows.filter(r => (r?.status ?? '').toString().trim() === statusFilter)
  }, [rows, statusFilter])

  const isInitialLoading = isLoading && !(infiniteData?.pages?.length)

  const loadLinkage = useCallback(
    async movement => {
      const id = movement.id
      if (!currentBusiness?.id || !id) return

      if (Array.isArray(movement.linkage) && movement.linkage.length > 0) {
        setLinkageByRow(prev => ({ ...prev, [id]: movement.linkage }))
        return
      }

      if (loadingDetails[id]) return
      setLoadingDetails(prev => ({ ...prev, [id]: true }))
      try {
        const response = await bankAPI.getDataLibros(currentBusiness.id, id)
        const linkage = response?.data?.linkage || []
        setLinkageByRow(prev => ({ ...prev, [id]: linkage }))
      } catch (err) {
        console.error('Error al cargar detalle de conciliación', err)
        setLinkageByRow(prev => ({ ...prev, [id]: [] }))
      } finally {
        setLoadingDetails(prev => ({ ...prev, [id]: false }))
      }
    },
    [bankAPI, currentBusiness?.id, loadingDetails]
  )

  // Prefetch linkages to mostrar el conteo sin necesidad de expandir (limitamos a los primeros 10 pendientes para no saturar)
  useEffect(() => {
    if (!rows.length) return
    const pending = rows
      .filter(r => linkageByRow[r.id] === undefined && !loadingDetails[r.id])
      .slice(0, 10)
    pending.forEach(loadLinkage)
  }, [rows, linkageByRow, loadingDetails, loadLinkage])

  const handleExport = async email => {
    if (!currentBusiness?.id) return
    setIsExporting(true)
    try {
      await businessAPI.exportBancoConciliaciones({
        businessId: currentBusiness.id,
        email,
        year: Number(yearFilter),
        month: Number(monthFilter)
      })
    } catch (err) {
      console.error('Error al exportar conciliaciones bancarias', err)
      throw err
    } finally {
      setIsExporting(false)
    }
  }

  const icons = [
    <button
      key='export-xlsx'
      className='w-10 h-10 text-green-700 font-bold cursor-pointer rounded-full shadow bg-white'
      onClick={() => setIsExportModalOpen(true)}
      disabled={!currentBusiness?.id}
    >
      XLS
    </button>
  ]

  const handleToggleRow = movement => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(movement.id)) {
        next.delete(movement.id)
      } else {
        next.add(movement.id)
        void loadLinkage(movement)
      }
      return next
    })
  }

  return (
    <div className='flex h-full min-h-0 w-full flex-col px-4'>
      <HeadingTitle
        title='Libro Banco - Conciliaciones'
        rightIcons={icons}
        iconHeight={32}
      />

      <div className='mb-4 flex shrink-0 flex-col gap-3'>
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end'>
        <FilterControl
          label='Mes'
          value={monthFilter}
          onChange={v => setMonthFilter(Number(v))}
          options={monthsOptionsWithAll}
          fullWidth
        />
        <FilterControl
          label='Año'
          value={yearFilter}
          onChange={v => setYearFilter(Number(v))}
          options={yearsOptions}
          fullWidth
        />
        <FilterControl
          label='Cuenta'
          value={accountFilter}
          onChange={setAccountFilter}
          options={accountOptions}
          fullWidth
        />
        <FilterControl
          label='Estado'
          value={statusFilter}
          onChange={setStatusFilter}
          options={statusOptions}
          fullWidth
        />
        <div className='w-full'>
          <SearchInput
            placeholder='Buscar por descripción'
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            classNameBorder='rounded-full'
          />
        </div>
      </div>
      </div>

      <TableBancoConciliaciones
        rows={filteredRows}
        expandedRows={expandedRows}
        onToggleRow={handleToggleRow}
        linkageByRow={linkageByRow}
        loadingDetails={loadingDetails}
        isLoading={isInitialLoading}
        onLoadMore={() => fetchNextPage()}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        lastElementRef={lastElementRef}
      />
      <ExportToXlsxModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExport}
        title='Exportar Conciliaciones Bancarias'
        description='Se enviará un Excel con los movimientos bancarios conciliados al correo indicado.'
        isLoading={isExporting}
      />
    </div>
  )
}

export default ListLibroBancoConciliaciones
